import { useQuery } from "@tanstack/react-query";
import { listAllFolders } from "../api/folders.api";
import type { BreadcrumbItem, FolderDTO, FolderIndexDTO } from "@titancloud/shared";

/** Lives under ["folders"], so every existing invalidation of folder listings refreshes it too. */
export const FOLDER_INDEX_KEY = ["folders", "index"] as const;

export interface FolderIndex {
  byId: Map<string, FolderDTO>;
  /** Children of each folder (null = root), in the server's name order. */
  children: Map<string | null, FolderDTO[]>;
}

function buildIndex(dto: FolderIndexDTO): FolderIndex | null {
  if (dto.truncated) return null;
  const byId = new Map<string, FolderDTO>();
  const children = new Map<string | null, FolderDTO[]>();
  for (const folder of dto.folders) {
    byId.set(folder.id, folder);
    const siblings = children.get(folder.parentId);
    if (siblings) siblings.push(folder);
    else children.set(folder.parentId, [folder]);
  }
  return { byId, children };
}

/**
 * The user's whole folder tree, loaded once and cached, so opening a folder or drawing its
 * breadcrumb needs no request. `index` is null while loading, after an error, or when the
 * account has too many folders for the server to send; callers then fall back to per-folder requests.
 */
export function useFolderIndex(): { index: FolderIndex | null; isPending: boolean } {
  const query = useQuery({
    queryKey: FOLDER_INDEX_KEY,
    queryFn: listAllFolders,
    select: buildIndex,
    staleTime: 60 * 1000,
  });
  return { index: query.data ?? null, isPending: query.isLoading };
}

/** Root-to-folder path from the index, or null when any folder on the way is missing from it. */
export function breadcrumbFromIndex(index: FolderIndex, folderId: string): BreadcrumbItem[] | null {
  const trail: BreadcrumbItem[] = [];
  let id: string | null = folderId;
  // Bounded by the folder count so a corrupt parent chain cannot loop forever.
  for (let steps = 0; id !== null && steps <= index.byId.size; steps++) {
    const folder = index.byId.get(id);
    if (!folder) return null;
    trail.push({ id: folder.id, name: folder.name, path: folder.path });
    id = folder.parentId;
  }
  return id === null ? trail.reverse() : null;
}
